import { create } from 'zustand';
import type { AdminStats, AdminUser, AdminTeam } from '../../../types';

interface AdminStore {
  // 状态
  stats: AdminStats | null;
  users: AdminUser[];
  teams: AdminTeam[];
  loading: boolean;
  error: string | null;

  // 操作
  loadStats: () => Promise<void>;
  loadUsers: (page?: number, limit?: number) => Promise<void>;
  loadTeams: (page?: number, limit?: number) => Promise<void>;
  updateUser: (userId: string, updates: Partial<AdminUser>) => Promise<void>;
  clearError: () => void;

  // 分页状态
  usersPage: number;
  usersTotalPages: number;
  teamsPage: number;
  teamsTotalPages: number;
}

export const useAdminStore = create<AdminStore>((set, get) => ({
  // 初始状态
  stats: null,
  users: [],
  teams: [],
  loading: false,
  error: null,
  usersPage: 1,
  usersTotalPages: 1,
  teamsPage: 1,
  teamsTotalPages: 1,

  // 加载系统统计
  loadStats: async () => {
    set({ loading: true, error: null });

    try {
      const { adminService } = await import('../services/adminService');
      const stats = await adminService.getSystemStats();
      set({ stats, loading: false });
    } catch (error: any) {
      set({
        loading: false,
        error: error.message || '加载统计数据失败'
      });
    }
  },

  // 加载用户列表
  loadUsers: async (page = 1, limit = 20) => {
    set({ loading: true, error: null });

    try {
      const { adminService } = await import('../services/adminService');
      const result = await adminService.getAllUsers(page, limit);
      set({
        users: result.users,
        usersPage: page,
        usersTotalPages: result.pages,
        loading: false
      });
    } catch (error: any) {
      set({
        loading: false,
        error: error.message || '加载用户列表失败'
      });
    }
  },

  // 加载团队列表
  loadTeams: async (page = 1, limit = 20) => {
    set({ loading: true, error: null });

    try {
      const { adminService } = await import('../services/adminService');
      const result = await adminService.getAllTeams(page, limit);
      set({
        teams: result.teams,
        teamsPage: page,
        teamsTotalPages: result.pages,
        loading: false
      });
    } catch (error: any) {
      set({
        loading: false,
        error: error.message || '加载团队列表失败'
      });
    }
  },

  // 更新用户
  updateUser: async (userId: string, updates: Partial<AdminUser>) => {
    set({ loading: true, error: null });

    try {
      const { adminService } = await import('../services/adminService');
      const updatedUser = await adminService.updateUser(userId, updates);

      // 更新本地状态
      const { users } = get();
      const updatedUsers = users.map(user =>
        user.id === userId ? updatedUser : user
      );

      set({ users: updatedUsers, loading: false });
    } catch (error: any) {
      set({
        loading: false,
        error: error.message || '更新用户失败'
      });
    }
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  }
}));